import ActionsButton                         from '@/components/actionsButton/ActionsButton';
import ApiCollection                         from '@widesk-core/models/ApiCollection';
import BackButton                            from '@/components/backButton/BackButton';
import { Box }                               from '@chakra-ui/react';
import CodeModel                             from '@/models/services/code/CodeModel';
import CodePropertyValueModel                from '@/models/services/code/CodePropertyValueModel';
import DeliverableTransferStatusHistoryModel from '@/models/services/shop/DeliverableTransferStatusHistoryModel';
import DeliverableTransferTypeModel          from '@/models/services/shop/DeliverableTransferTypeModel';
import React                                 from 'react';
import useApiCollection                      from '@widesk-core/hooks/useApiCollection';
import { useTranslation }                    from 'react-i18next';
import useTranslations                       from '@/hooks/useTranslations';
import { ModalData }                         from '@/components/modal/Modal';
import { isTicketTransferable }              from '@/features/ticketTransfer/isTicketTransferable';
import { openTicketTransferModal }           from '@/features/ticketTransfer/openTicketTransferModal';
import { toaster }                           from '@/components/ui/toaster';

interface ITicketPageHeaderProps {
	code: CodeModel;
	codePropertyValueCollection: ApiCollection<CodePropertyValueModel>;
	ticketHasBeenTransfered: boolean;
	transferStatusHistoryCollection: ApiCollection<DeliverableTransferStatusHistoryModel>;
	setTicketHasBeenTransfered: React.Dispatch<React.SetStateAction<boolean>>;
	setTransferEmailDisplayed: React.Dispatch<React.SetStateAction<string>>;
	setModalData: React.Dispatch<React.SetStateAction<ModalData | undefined>>;
	setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const TicketPageHeader = (props: ITicketPageHeaderProps) => {
	const {
		code,
		codePropertyValueCollection,
		ticketHasBeenTransfered,
		transferStatusHistoryCollection,
		setTicketHasBeenTransfered,
		setTransferEmailDisplayed,
		setModalData,
		setModalIsOpen,
	} = props;

	const { getNavigationTranslation } = useTranslations();
	const deliverableTransferTypeCollection = useApiCollection(DeliverableTransferTypeModel);
	const { t } = useTranslation();

	const ownerEmail =
		codePropertyValueCollection
			.find(codePropertyValue => codePropertyValue.codeProperty.reference === 'owner_email')
			?.value || '';
		
	const transferStatusHistory =
		transferStatusHistoryCollection?.models?.[0] || undefined;

	const isTransferable = isTicketTransferable({
		codeKindReference: code.codeKind.reference,
		ticketHasBeenTransfered,
		isBackedByDeliverable: code.isDeliverable || code.isDeliverableExchange,
		isFromShop: !!code?.isFromShop,
		transferStatusHistory,
	});

	const transferTicket = async () => {
		if (!code.deliverableUrn) return;

		openTicketTransferModal({
			codeKindReference: code.codeKind.reference,
			ownerEmail,
			deliverableUrn: code.deliverableUrn,
			deliverableTransferTypeCollection,
			setModalData,
			setModalIsOpen,
			onSuccess: (email) => {
				setTicketHasBeenTransfered(true);
				setTransferEmailDisplayed(email);
			},
		});
	};

	const downloadTicket = async () => {
		try {
			await code.download();
		} catch (error) {
			const key = error instanceof Error && error.message === 'noFile'
				? 'pages.ticket.actions.download.noFile'
				: 'pages.ticket.actions.download.error';
			toaster.error({ title: t(key) });
		}
	};

	const actions: Array<{ label: string; do: () => Promise<void> }> = [];

	if (code.isDownloadable) {
		actions.push({
			label: t('pages.ticket.actions.download.label'),
			do: downloadTicket,
		});
	}

	if (isTransferable) {
		actions.push({
			label: t('pages.ticket.actions.transfer.label'),
			do: transferTicket,
		});
	}

	return (
		<Box display="flex" alignItems="center" justifyContent="space-between">
			<BackButton to={getNavigationTranslation('tickets', 'path')} />
			{actions.length > 0 && <ActionsButton actions={actions} />}
		</Box>
	);
};

export default TicketPageHeader;
